<template>
  <div class="form-builder-admin">
    <div class="header">
      <h1 class="title">Form Builder</h1>
      <button @click="createNewForm" class="btn-create">
        <v-icon>mdi-plus</v-icon> Create New Form
      </button>
    </div>

    <!-- Forms List -->
    <div class="forms-container">
      <div v-if="loading" class="loading">
        <div class="loader"></div>
        <p>Loading forms...</p>
      </div>

      <div v-else-if="forms.length === 0" class="empty-state">
        <div class="empty-icon">📋</div>
        <h2>No forms yet</h2>
        <p>Create your first form to get started</p>
      </div>

      <div v-else class="forms-grid">
        <div v-for="form in forms" :key="form.id" class="form-card">
          <div class="form-card-header">
            <h3 class="form-title">{{ form.title }}</h3>
            <div class="form-actions">
              <button @click="editForm(form)" class="btn-icon" title="Edit">
                <v-icon small>mdi-pencil</v-icon>
              </button>
              <button @click="deleteForm(form.id)" class="btn-icon btn-danger" title="Delete">
                <v-icon small>mdi-delete</v-icon>
              </button>
            </div>
          </div>
          <p class="form-description">{{ form.description || 'No description' }}</p>
          <div class="form-meta">
            <span class="meta-item">
              <v-icon small>mdi-form-select</v-icon>
              {{ form.questions ? form.questions.length : 0 }} questions
            </span>
            <span class="meta-item">
              <v-icon small>mdi-calendar</v-icon>
              {{ formatDate(form.createdAt) }}
            </span>
          </div>
          <div class="form-footer">
            <button @click="viewResponses(form.id)" class="btn-secondary">
              <v-icon small>mdi-chart-bar</v-icon> View Responses
            </button>
            <button @click="copyFormLink(form.id)" class="btn-primary">
              <v-icon small>mdi-link</v-icon> Copy Link
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Form Editor Modal -->
    <div v-if="showEditor" class="modal-overlay" @click="closeEditor">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h2>{{ editingForm ? 'Edit Form' : 'Create New Form' }}</h2>
          <button @click="closeEditor" class="btn-close">×</button>
        </div>

        <div class="modal-body">
          <div class="form-group">
            <label>Form Title</label>
            <input v-model="currentForm.title" type="text" class="input" placeholder="Enter form title" />
          </div>

          <div class="form-group">
            <label>Description</label>
            <textarea v-model="currentForm.description" class="textarea" placeholder="Enter form description"></textarea>
          </div>

          <div class="questions-section">
            <div class="section-header">
              <h3>Questions</h3>
              <button @click="addQuestion" class="btn-add-question">
                <v-icon small>mdi-plus</v-icon> Add Question
              </button>
            </div>

            <draggable v-model="currentForm.questions" class="questions-list" handle=".drag-handle">
              <div v-for="(question, index) in currentForm.questions" :key="index" class="question-item">
                <div class="question-header">
                  <span class="drag-handle">☰</span>
                  <input v-model="question.label" type="text" class="input" placeholder="Question" />
                  <select v-model="question.type" class="select">
                    <option value="text">Short Text</option>
                    <option value="textarea">Long Text</option>
                    <option value="email">Email</option>
                    <option value="tel">Phone</option>
                    <option value="radio">Multiple Choice</option>
                    <option value="checkbox">Checkboxes</option>
                    <option value="select">Dropdown</option>
                  </select>
                  <button @click="removeQuestion(index)" class="btn-remove">×</button>
                </div>

                <!-- Options for radio, checkbox, select -->
                <div v-if="['radio', 'checkbox', 'select'].includes(question.type)" class="options-section">
                  <div v-for="(option, optIndex) in question.options" :key="optIndex" class="option-item">
                    <input v-model="question.options[optIndex]" type="text" class="input-sm" placeholder="Option" />
                    <button @click="removeOption(index, optIndex)" class="btn-remove-sm">×</button>
                  </div>
                  <button @click="addOption(index)" class="btn-add-option">+ Add Option</button>
                </div>

                <div class="question-footer">
                  <label class="checkbox-label">
                    <input type="checkbox" v-model="question.required" />
                    <span>Required</span>
                  </label>
                </div>
              </div>
            </draggable>
          </div>
        </div>

        <div class="modal-footer">
          <button @click="closeEditor" class="btn-cancel">Cancel</button>
          <button @click="saveForm" class="btn-save">Save Form</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { db } from '../firebase'
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, serverTimestamp, query, orderBy } from 'firebase/firestore'
import draggable from 'vuedraggable'

export default {
  name: 'FormBuilderPage',
  components: {
    draggable
  },
  data() {
    return {
      forms: [],
      loading: true,
      showEditor: false,
      editingForm: null,
      currentForm: {
        title: '',
        description: '',
        questions: []
      }
    }
  },
  async mounted() {
    await this.loadForms()
  },
  methods: {
    async loadForms() {
      try {
        this.loading = true
        const q = query(collection(db, 'forms'), orderBy('createdAt', 'desc'))
        const querySnapshot = await getDocs(q)
        
        this.forms = []
        querySnapshot.forEach((doc) => {
          this.forms.push({
            id: doc.id,
            ...doc.data()
          })
        })
      } catch (error) {
        console.error('Error loading forms:', error)
        alert('Error loading forms')
      } finally {
        this.loading = false
      }
    },
    createNewForm() {
      this.editingForm = null
      this.currentForm = {
        title: 'Untitled Form',
        description: '',
        questions: []
      }
      this.showEditor = true
    },
    editForm(form) {
      this.editingForm = form.id
      this.currentForm = {
        title: form.title,
        description: form.description || '',
        questions: form.questions ? JSON.parse(JSON.stringify(form.questions)) : []
      }
      this.showEditor = true
    },
    async deleteForm(formId) {
      if (!confirm('Are you sure you want to delete this form?')) return
      
      try {
        await deleteDoc(doc(db, 'forms', formId))
        await this.loadForms()
        alert('Form deleted successfully')
      } catch (error) {
        console.error('Error deleting form:', error)
        alert('Error deleting form')
      }
    },
    async saveForm() {
      if (!this.currentForm.title) {
        alert('Please enter a form title')
        return
      }

      try {
        if (this.editingForm) {
          // Update existing form
          await updateDoc(doc(db, 'forms', this.editingForm), {
            title: this.currentForm.title,
            description: this.currentForm.description,
            questions: this.currentForm.questions,
            updatedAt: serverTimestamp()
          })
        } else {
          // Create new form
          await addDoc(collection(db, 'forms'), {
            title: this.currentForm.title,
            description: this.currentForm.description,
            questions: this.currentForm.questions,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
          })
        }
        
        this.closeEditor()
        await this.loadForms()
        alert('Form saved successfully')
      } catch (error) {
        console.error('Error saving form:', error)
        alert('Error saving form')
      }
    },
    closeEditor() {
      this.showEditor = false
      this.editingForm = null
    },
    addQuestion() {
      this.currentForm.questions.push({
        label: '',
        type: 'text',
        required: false,
        options: []
      })
    },
    removeQuestion(index) {
      this.currentForm.questions.splice(index, 1)
    },
    addOption(questionIndex) {
      if (!this.currentForm.questions[questionIndex].options) {
        this.currentForm.questions[questionIndex].options = []
      }
      this.currentForm.questions[questionIndex].options.push('')
    },
    removeOption(questionIndex, optionIndex) {
      this.currentForm.questions[questionIndex].options.splice(optionIndex, 1)
    },
    viewResponses(formId) {
      this.$router.push(`/form-responses/${formId}`)
    },
    copyFormLink(formId) {
      const link = `${window.location.origin}/form/${formId}`
      navigator.clipboard.writeText(link)
      alert('Form link copied to clipboard!')
    },
    formatDate(timestamp) {
      if (!timestamp) return 'N/A'
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp)
      return date.toLocaleDateString()
    }
  }
}
</script>

<style scoped>
.form-builder-admin {
  min-height: 100vh;
  background: #f5f5f5;
  padding: 40px 20px;
}

.header {
  max-width: 1200px;
  margin: 0 auto 40px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.title {
  font-size: 32px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0;
}

.btn-create {
  padding: 12px 24px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s;
  box-shadow: 0 4px 15px rgba(103, 58, 183, 0.3);
}

.btn-create:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(103, 58, 183, 0.4);
}

.forms-container {
  max-width: 1200px;
  margin: 0 auto;
}

.loading {
  background: white;
  padding: 80px;
  border-radius: 12px;
  text-align: center;
}

.loader {
  width: 50px;
  height: 50px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #673ab7;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.empty-state {
  background: white;
  padding: 80px 40px;
  border-radius: 12px;
  text-align: center;
}

.empty-icon {
  font-size: 64px;
  margin-bottom: 20px;
}

.empty-state h2 {
  font-size: 24px;
  color: #1a1a1a;
  margin: 0 0 12px 0;
}

.empty-state p {
  font-size: 16px;
  color: #666;
  margin: 0;
}

.forms-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 24px;
}

.form-card {
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  transition: all 0.3s;
}

.form-card:hover {
  box-shadow: 0 4px 16px rgba(0,0,0,0.15);
  transform: translateY(-4px);
}

.form-card-header {
  display: flex;
  justify-content: space-between;
  align-items: start;
  margin-bottom: 12px;
}

.form-title {
  font-size: 20px;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0;
  flex: 1;
}

.form-actions {
  display: flex;
  gap: 8px;
}

.btn-icon {
  width: 32px;
  height: 32px;
  border: none;
  background: #f5f5f5;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.btn-icon:hover {
  background: #e0e0e0;
}

.btn-danger:hover {
  background: #fee;
  color: #d32f2f;
}

.form-description {
  font-size: 14px;
  color: #666;
  margin: 0 0 16px 0;
  line-height: 1.5;
}

.form-meta {
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid #f0f0f0;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #999;
}

.form-footer {
  display: flex;
  gap: 8px;
}

.btn-primary,
.btn-secondary {
  flex: 1;
  padding: 10px 16px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition: all 0.2s;
}

.btn-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.btn-primary:hover {
  opacity: 0.9;
}

.btn-secondary {
  background: white;
  color: #673ab7;
  border: 1px solid #673ab7;
}

.btn-secondary:hover {
  background: #f8f5fb;
}

/* Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.modal-content {
  background: white;
  border-radius: 12px;
  max-width: 900px;
  width: 100%;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 10px 40px rgba(0,0,0,0.3);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px;
  border-bottom: 1px solid #e0e0e0;
}

.modal-header h2 {
  margin: 0;
  font-size: 24px;
  font-weight: 600;
}

.btn-close {
  width: 40px;
  height: 40px;
  border: none;
  background: #f5f5f5;
  border-radius: 50%;
  font-size: 24px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.btn-close:hover {
  background: #e0e0e0;
}

.modal-body {
  padding: 24px;
  overflow-y: auto;
  flex: 1;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
}

.input,
.textarea,
.select {
  width: 100%;
  padding: 10px 14px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 15px;
  font-family: inherit;
  transition: all 0.2s;
}

.input:focus,
.textarea:focus,
.select:focus {
  outline: none;
  border-color: #673ab7;
  box-shadow: 0 0 0 3px rgba(103, 58, 183, 0.1);
}

.textarea {
  min-height: 80px;
  resize: vertical;
}

.questions-section {
  margin-top: 30px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.section-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
}

.btn-add-question {
  padding: 8px 16px;
  background: white;
  color: #673ab7;
  border: 2px dashed #673ab7;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s;
}

.btn-add-question:hover {
  background: #f8f5fb;
}

.questions-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.question-item {
  background: #f8f9fa;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 16px;
}

.question-header {
  display: flex;
  gap: 12px;
  align-items: center;
  margin-bottom: 12px;
}

.drag-handle {
  cursor: move;
  color: #999;
  font-size: 18px;
}

.question-header .input {
  flex: 1;
}

.question-header .select {
  min-width: 150px;
}

.btn-remove {
  width: 32px;
  height: 32px;
  border: none;
  background: #fee;
  color: #d32f2f;
  border-radius: 6px;
  font-size: 20px;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-remove:hover {
  background: #fdd;
}

.options-section {
  margin: 12px 0;
  padding-left: 30px;
}

.option-item {
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
}

.input-sm {
  flex: 1;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
}

.btn-remove-sm {
  width: 28px;
  height: 28px;
  border: none;
  background: #fee;
  color: #d32f2f;
  border-radius: 4px;
  font-size: 18px;
  cursor: pointer;
}

.btn-add-option {
  padding: 6px 12px;
  background: white;
  border: 1px dashed #ddd;
  border-radius: 4px;
  font-size: 13px;
  color: #666;
  cursor: pointer;
}

.btn-add-option:hover {
  border-color: #673ab7;
  color: #673ab7;
}

.question-footer {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #e0e0e0;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #666;
  cursor: pointer;
}

.checkbox-label input[type="checkbox"] {
  width: 16px;
  height: 16px;
  cursor: pointer;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 24px;
  border-top: 1px solid #e0e0e0;
}

.btn-cancel,
.btn-save {
  padding: 12px 24px;
  border: none;
  border-radius: 6px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-cancel {
  background: #f5f5f5;
  color: #666;
}

.btn-cancel:hover {
  background: #e0e0e0;
}

.btn-save {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.btn-save:hover {
  opacity: 0.9;
}

@media (max-width: 768px) {
  .forms-grid {
    grid-template-columns: 1fr;
  }

  .header {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
  }

  .btn-create {
    width: 100%;
    justify-content: center;
  }

  .modal-content {
    max-height: 95vh;
  }

  .question-header {
    flex-wrap: wrap;
  }

  .question-header .select {
    width: 100%;
  }
}
</style>
